import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllInvoiceByUserIdCount, getAllInvoiceFreelanceUser } from '../../redux/Thunk/FreelanceScreenThunk/FreelanceThunk';
import { Box, Card, CardContent, CardHeader, Chip, IconButton, Tab, Tabs, Typography } from '@mui/material';
import SearchField from '../../Components/Ui_elements/SearchField';
import { TabPanel } from '../../Components/TabPanel';
import GenericTable from '../../Components/Ui_elements/GenericTable';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { invoiceTabsCountFreelance } from '../../Helper/Constants';
import Btn from '../../Components/Ui_elements/Btn';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { localizeNumber } from '../../Helper/generalhelper';

const FreelanceInvoice = () => {
    const { freelance_list, invoice_tab_counts } = useSelector((state) => state.root.FreelanceModule);
    const { user } = useSelector((state) => state?.root?.authUser);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [order, setOrder] = useState({ column: null, dir: "asc" });
    const [searchTerm, setSearchTerm] = useState("");
    const [search, setSearch] = useState("");
    const [tabValue, setTabValue] = useState('all'); // Default to 'all' tab

    // Extract the counts from the API response
    const counts = invoice_tab_counts?.data?.[0] || {};

    useEffect(() => {
        dispatch(getAllInvoiceByUserIdCount(user?.iUserId));
    }, [dispatch, user?.iUserId]);

    const fetchData = useCallback(async () => {
        try {
            await dispatch(
                getAllInvoiceFreelanceUser({
                    id: user?.iUserId,
                    page: page +1,
                    perPage: pageSize,
                    tabName: tabValue,
                    search: search,
                    sortBy: order.column,
                    sortOrder: order.column ? order.dir.toLowerCase() : null,
                })
            );
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
    }, [tabValue, page, pageSize, order, search, dispatch, user?.iUserId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(
        () => [
            {
                name: "index",
                label: "#",
                options: {
                    customBodyRender: (_, tableMeta) => (
                        <Typography variant="body2" noWrap>
                            {tableMeta.rowIndex + 1}
                        </Typography>
                    ),
                    setCellProps: () => ({
                        style: {
                            width: "50px",
                            paddingLeft: "16px",
                        },
                    }),
                    setCellHeaderProps: () => ({
                        style: {
                            paddingLeft: "16px",
                        },
                    }),

                },
            },
            {
                name: "custom_invoice_no",
                sortPramas: "custom_invoice_no",
                label: "Invoice Number",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        const invoiceId = freelance_list?.data[tableMeta.rowIndex]?.invoice_id;
                        return (
                            <Typography
                                sx={{ cursor: "pointer", color: "blue" }}
                                onClick={() => navigate(`/viewinvoicelinguist/${invoiceId}`)}
                            >
                                {value}
                            </Typography>
                        );
                    },
                    setCellProps: () => ({ style: { width: "150px" } }),
                },
            },
            {
                name: "freelanceName",
                sortPramas: "freelanceName",
                label: "Freelance Name",
            },
            {
                name: "Invoice_cost",
                sortPramas: "Invoice_cost",
                label: "Account", 
                options: {
                    customBodyRender: (value) => localizeNumber(value),
                },
            },
            {
                name: "freelance_currency",
                sortPramas: "freelance_currency",
                label: "Currency",
            },
            {
                name: "invoice_date",
                label: "Creation Date",
                sortPramas: "invoice_date",
                options: {
                    customBodyRender: (value) => {
                        if (value === "0000-00-00 00:00:00") {
                            return <Typography variant="body2"></Typography>;
                        }
                        const formattedDate = dayjs(value).format("DD.MM.YYYY");
                        return (
                            <Typography variant="body2" noWrap>
                                {formattedDate}
                            </Typography>
                        );
                    },
                },
            },
            {
                name: "inv_due_date",
                label: "Due Date",
                sortPramas: "inv_due_date",
                options: {
                  customBodyRender: (value) => {
                    if (
                      !value ||
                      value === "0000-00-00 00:00:00" ||
                      value === "0000-00-00"
                    ) {
                      return <Typography variant="body2" />;
                    }
              
                    const date = dayjs(value);
              
                    if (!date.isValid()) {
                      return <Typography variant="body2" />;
                    }
              
                    return (
                      <Typography variant="body2" noWrap>
                        {date.format("DD.MM.YYYY")}
                      </Typography>
                    );
                  },
                }
            },
            {
                name: "paid_date",
                label: "Payment Date",
                sortPramas: "paid_date",
                options: {
                    customBodyRender: (value) => {
                        if (value === "0000-00-00 00:00:00") {
                            return <Typography variant="body2"></Typography>;
                        }
                        const formattedDate = dayjs(value).format("DD.MM.YYYY");
                        const formattedTime = dayjs(value).format("HH:mm");
                        return (
                            <Typography variant="body2" noWrap>
                                {formattedDate} | {formattedTime}
                            </Typography>
                        );
                    },
                },
            },
            {
                name: "invoice_status",
                sortPramas: "invoice_status",
                label: "Invoice Status",
            },
        ],
        [freelance_list?.data]
    );

    const handleDebouncedSearch = useCallback(
        debounce((value) => {
            setSearch(value);
            setPage(0);
        }, 500),
        []
    );

    useEffect(() => {
        return () => {
            handleDebouncedSearch.cancel();
        };
    }, [handleDebouncedSearch]);

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue);
        setPage(0); // Reset to first page when changing tabs
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        handleDebouncedSearch(value);
    };

    const formatLabel = (label) => {
        return label
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase());
    };

    // Map tab values to count keys in the API response
    const getCountForTab = (tabValue) => {
        switch (tabValue) {
            case 'all':
                return counts.allinvoice || 0;
            case 'waiting_on_approval':
                return counts.waitingApproval || 0;
            case 'outstanding':
                return counts.outstanding || 0;
            case 'overdue':
                return counts.overdue || 0;
            case 'paid':
                return counts.paid || 0;
            case 'cancelled':
                return counts.cancelled || 0;
            default:
                return 0;
        }
    };

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">All Invoices</Typography>
                <Grid item>
                    <Btn sx={{ mr: 2 }} onClick={() => navigate('/invoice-linguist-jobs')} startIcon={<AddIcon />}>
                        Create Invoice
                    </Btn>
                    <Btn onClick={() => window.history.back()} startIcon={<ArrowBackIcon />}>
                        Back
                    </Btn>
                </Grid>
            </Box>

            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Invoices Tabs"
            >
                {invoiceTabsCountFreelance?.map((navItem) => {
                    const count = getCountForTab(navItem.value);

                    return (
                        <Tab
                            key={navItem.value}
                            value={navItem.value}
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography textTransform="none">
                                        {formatLabel(navItem.label)}
                                    </Typography>
                                    <Chip
                                        label={count}
                                        sx={{
                                            ml: 1,
                                            backgroundColor: tabValue === navItem.value ? '#fff' : 'primary.main',
                                            color: tabValue === navItem.value ? 'primary.main' : 'white',
                                            border: tabValue === navItem.value ? '1px solid primary.main' : 'none',
                                            '& .MuiChip-label': {
                                                px: 1,
                                            }
                                        }}
                                    />
                                </Box>
                            }
                            aria-label={`${navItem.label} Tab`}
                        />
                    );
                })}
            </Tabs>

            <Card>
                <CardHeader
                    title=""
                    action={
                        <Box display={"flex"} alignItems={"center"} gap={2}>
                            <SearchField
                                onSearch={handleSearch}
                                placeholder="Search..."
                                size="small"
                                initialValue={searchTerm}
                            />
                        </Box>
                    }
                />
            </Card>


            <CardContent sx={{ padding: "0px" }}>
                {invoiceTabsCountFreelance?.map((tab) => (
                    <TabPanel key={tab.value} value={tabValue} index={tab.value}>
                        <GenericTable
                            columns={columns}
                            data={freelance_list?.data || []}
                            showAction={false}
                            options={{
                                selectableRows: "none",
                                serverSide: true,
                                count: freelance_list?.totalRecode || 0,
                                page,
                                rowsPerPage: pageSize,
                                rowsPerPageOptions: [10, 25, 50, 100],
                                search: false,
                                download: false,
                                print: false,
                                filter: false,
                                viewColumns: false,
                                sortOrder: {
                                    name: columns[order?.column]?.name || "",
                                    direction: order?.dir || "asc",
                                },
                                onTableChange: (action, tableState) => {
                                    switch (action) {
                                        case "changePage":
                                            setPage(tableState.page);
                                            break;
                                        case "changeRowsPerPage":
                                            setPageSize(tableState.rowsPerPage);
                                            setPage(0);
                                            break;
                                        case "sort":
                                            setOrder({
                                                column: tableState.activeColumn,
                                                dir: tableState.sortOrder.direction,
                                            });
                                            break;
                                        default:
                                            break;
                                    }
                                },
                            }}
                        />
                    </TabPanel>
                ))}
            </CardContent>

        </>
    );
};

export default FreelanceInvoice;
